'use client'

/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useApplicationState } from '../../hooks/common/use-application-state'
import { cypressId } from '../../utils/cypress-attribute'
import { Logger } from '../../utils/logger'
import {
  buildNoteTree,
  getAncestorFolderPaths,
  getNoteLabel,
  isTopNotePath,
  type NoteTreeFolder
} from './build-note-tree'
import { NoteTreeFolderItem } from './note-tree-folder-item'
import { NoteTreeNoteItem } from './note-tree-note-item'
import styles from './note-tree-sidebar.module.scss'
import { useNoteTreeRefresh } from './use-note-tree-refresh'
import { useNoteTreeSidebarAvailable } from './use-note-tree-sidebar-available'
import { useSyncCurrentNoteIntoTree } from './use-sync-current-note-into-tree'
import { deleteNote } from '../../api/notes'
import { removeNoteTreeEntry } from '../../redux/note-tree/methods'
import { DeleteNoteModal } from '../editor-page/sidebar/specific-sidebar-entries/delete-note-sidebar-entry/delete-note-modal'
import { useUiNotifications } from '../notifications/ui-notification-boundary'
import { type ContextMenuPosition, NoteTreeContextMenu } from './note-tree-context-menu'
import { PageSettingsModal } from '../common/page-settings-modal/page-settings-modal'
import type { NoteTreeEntryInterface } from '@hedgedoc/commons'
import { usePathname, useRouter } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

const logger = new Logger('NoteTreeSidebar')

const EXPANDED_FOLDERS_LOCAL_STORAGE_KEY = 'noteTreeExpandedFolders'
const UNFILED_FOLDER_PATH = '\u0000unfiled'

const loadExpandedFolders = (): Set<string> => {
  try {
    const storedValue = window.localStorage.getItem(EXPANDED_FOLDERS_LOCAL_STORAGE_KEY)
    const parsed: unknown = storedValue === null ? [] : JSON.parse(storedValue)
    return new Set(Array.isArray(parsed) ? parsed.filter((path) => typeof path === 'string') : [])
  } catch (error) {
    logger.error('Reading from local storage failed', error)
    return new Set()
  }
}

const saveExpandedFolders = (expandedFolders: Set<string>): void => {
  try {
    window.localStorage.setItem(EXPANDED_FOLDERS_LOCAL_STORAGE_KEY, JSON.stringify([...expandedFolders]))
  } catch (error) {
    logger.error('Saving to local storage failed', error)
  }
}

/**
 * Renders the navigation sidebar with a collapsible tree of all notes the user can read,
 * grouped into folders by the `path` in their frontmatter.
 * The component also keeps the tree data up to date, so it should always be mounted.
 */
export const NoteTreeSidebar: React.FC = () => {
  useNoteTreeRefresh()
  useSyncCurrentNoteIntoTree()
  const { t } = useTranslation()

  const available = useNoteTreeSidebarAvailable()
  const open = useApplicationState((state) => state.noteTree.sidebarOpen)
  const loaded = useApplicationState((state) => state.noteTree.loaded)
  const entries = useApplicationState((state) => state.noteTree.entries)
  const pathname = usePathname() ?? ''
  const noteAlias = useApplicationState((state) => state.noteDetails?.primaryAlias)
  const currentAlias = pathname.startsWith('/n/') ? noteAlias : undefined
  const currentPath = currentAlias === undefined ? undefined : entries[currentAlias]?.path

  const [filter, setFilter] = useState('')
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    setExpandedFolders(loadExpandedFolders())
  }, [])

  // Expand the folders that lead to the currently opened note
  useEffect(() => {
    if (currentPath === undefined || isTopNotePath(currentPath)) {
      return
    }
    const folderPaths = currentPath === '' ? [UNFILED_FOLDER_PATH] : getAncestorFolderPaths(currentPath)
    setExpandedFolders((previous) => {
      if (folderPaths.every((path) => previous.has(path))) {
        return previous
      }
      const next = new Set([...previous, ...folderPaths])
      saveExpandedFolders(next)
      return next
    })
  }, [currentPath])

  const onToggle = useCallback((path: string) => {
    setExpandedFolders((previous) => {
      const next = new Set(previous)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      saveExpandedFolders(next)
      return next
    })
  }, [])

  const trimmedFilter = filter.trim().toLowerCase()
  const isExpanded = useCallback(
    (path: string) => trimmedFilter !== '' || expandedFolders.has(path),
    [expandedFolders, trimmedFilter]
  )

  const tree = useMemo(() => {
    const filteredEntries = Object.values(entries).filter(
      (entry) =>
        trimmedFilter === '' ||
        getNoteLabel(entry).toLowerCase().includes(trimmedFilter) ||
        entry.primaryAlias.toLowerCase().includes(trimmedFilter)
    )
    return buildNoteTree(filteredEntries)
  }, [entries, trimmedFilter])

  const unfiledFolder = useMemo<NoteTreeFolder>(
    () => ({ name: t('noteTree.unfiled'), path: UNFILED_FOLDER_PATH, folders: [], notes: tree.unfiled }),
    [t, tree.unfiled]
  )

  const [contextMenu, setContextMenu] = useState<{
    note: NoteTreeEntryInterface
    position: ContextMenuPosition
  } | null>(null)
  const [noteToDelete, setNoteToDelete] = useState<NoteTreeEntryInterface | null>(null)
  const [noteToEdit, setNoteToEdit] = useState<NoteTreeEntryInterface | null>(null)
  const onHideEditModal = useCallback(() => setNoteToEdit(null), [])
  const editInitialSettings = useMemo(
    () =>
      noteToEdit === null ? undefined : { title: noteToEdit.title, path: noteToEdit.path, badge: noteToEdit.badge },
    [noteToEdit]
  )
  const router = useRouter()
  const { showErrorNotificationBuilder } = useUiNotifications()

  useEffect(() => {
    setContextMenu(null)
  }, [pathname])

  const onNoteContextMenu = useCallback((note: NoteTreeEntryInterface, position: ContextMenuPosition) => {
    setContextMenu({ note, position })
  }, [])
  const onCloseContextMenu = useCallback(() => setContextMenu(null), [])

  const onFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value)
    setContextMenu(null)
  }, [])
  const onHideDeleteModal = useCallback(() => setNoteToDelete(null), [])

  const onConfirmDelete = useCallback(
    (keepMedia: boolean) => {
      if (noteToDelete === null) {
        return
      }
      const alias = noteToDelete.primaryAlias
      deleteNote(alias, keepMedia)
        .then(() => {
          removeNoteTreeEntry(alias)
          if (alias === currentAlias) {
            router.push('/')
          }
        })
        .catch(showErrorNotificationBuilder('editor.modal.deleteNote.error'))
        .finally(() => setNoteToDelete(null))
    },
    [currentAlias, noteToDelete, router, showErrorNotificationBuilder]
  )

  if (!available || !open) {
    return null
  }

  const isEmpty = tree.top.length === 0 && tree.folders.length === 0 && tree.unfiled.length === 0

  return (
    <aside className={styles.sidebar} aria-label={t('noteTree.title')} {...cypressId('note-tree-sidebar')}>
      <div className={styles.header}>
        <Form.Control
          size={'sm'}
          type={'search'}
          value={filter}
          onChange={onFilterChange}
          placeholder={t('noteTree.filter')}
          aria-label={t('noteTree.filter')}
        />
      </div>
      <nav className={styles.tree}>
        {loaded && isEmpty && (
          <p className={styles.empty}>{trimmedFilter === '' ? t('noteTree.empty') : t('noteTree.noMatches')}</p>
        )}
        <ul>
          {tree.top.map((note) => (
            <NoteTreeNoteItem
              key={note.primaryAlias}
              note={note}
              depth={0}
              active={note.primaryAlias === currentAlias}
              onContextMenu={onNoteContextMenu}
            />
          ))}
          {tree.folders.map((folder) => (
            <NoteTreeFolderItem
              key={folder.path}
              folder={folder}
              depth={0}
              isExpanded={isExpanded}
              onToggle={onToggle}
              currentAlias={currentAlias}
              onNoteContextMenu={onNoteContextMenu}
            />
          ))}
          {tree.unfiled.length > 0 && (
            <NoteTreeFolderItem
              folder={unfiledFolder}
              depth={0}
              isExpanded={isExpanded}
              onToggle={onToggle}
              currentAlias={currentAlias}
              onNoteContextMenu={onNoteContextMenu}
              allowNewNote={false}
            />
          )}
        </ul>
      </nav>
      {contextMenu !== null && (
        <NoteTreeContextMenu
          note={contextMenu.note}
          position={contextMenu.position}
          onClose={onCloseContextMenu}
          onDelete={setNoteToDelete}
          onEdit={setNoteToEdit}
        />
      )}
      {noteToEdit !== null && editInitialSettings !== undefined && (
        <PageSettingsModal
          show={true}
          onHide={onHideEditModal}
          mode={'edit'}
          noteAlias={noteToEdit.primaryAlias}
          initialSettings={editInitialSettings}
        />
      )}
      <DeleteNoteModal
        optionalNoteTitle={noteToDelete === null ? undefined : getNoteLabel(noteToDelete)}
        overrideIsOwner={true}
        show={noteToDelete !== null}
        onHide={onHideDeleteModal}
        onConfirm={onConfirmDelete}
      />
    </aside>
  )
}
